const RESET_PASSWORD_TOKEN_PATH = /(\/(?:reset\/password|password\/token)\/)[^?#\s]+/g;

/**
 * Replaces password reset tokens in a URL or path with `*`.
 *
 * For example, `/reset/password/abc` becomes `/reset/password/*`.
 *
 * @param {*} value The value to scrub. Non-strings are returned unchanged.
 * @return {*} The scrubbed string, or the original value if it is not a string.
 */
export function scrubUrl(value) {
  if (typeof value === 'string') {
    return value.replace(RESET_PASSWORD_TOKEN_PATH, '$1*');
  }
  return value;
}

/**
 * Scrubs the request URL and Referer header of an event in place.
 *
 * @param {!Sentry.Event} event The Sentry event.
 * @return {!Sentry.Event} The same event, scrubbed.
 */
export function scrubRequest(event) {
  if (event.request) {
    if (event.request.url) {
      event.request.url = scrubUrl(event.request.url);
    }

    if (event.request.headers && event.request.headers.Referer) {
      event.request.headers.Referer = scrubUrl(event.request.headers.Referer);
    }
  }
  return event;
}

/**
 * Scrubs the `from`, `to` and `url` fields of a breadcrumb's data in place.
 *
 * Used as the `beforeBreadcrumb` hook, and by `scrubRecordingEvent` for the
 * breadcrumbs that replay builds itself.
 *
 * @param {!Sentry.Breadcrumb} breadcrumb The Sentry breadcrumb.
 * @return {!Sentry.Breadcrumb} The same breadcrumb, scrubbed.
 */
export function scrubBreadcrumb(breadcrumb) {
  if (breadcrumb.data) {
    ['from', 'to', 'url'].forEach((field) => {
      if (breadcrumb.data[field]) {
        breadcrumb.data[field] = scrubUrl(breadcrumb.data[field]);
      }
    });
  }

  return breadcrumb;
}

/**
 * Scrubs `tags.transaction`, the request URL and the Referer header of an
 * error event in place.
 *
 * Used as the `beforeSend` hook. Breadcrumb URLs are handled by
 * `scrubBreadcrumb`.
 *
 * @param {!Sentry.Event} event The Sentry error event.
 * @return {!Sentry.Event} The same event, scrubbed.
 */
export function scrubErrorEvent(event) {
  if (event.tags && event.tags.transaction) {
    event.tags.transaction = scrubUrl(event.tags.transaction);
  }

  return scrubRequest(event);
}

/**
 * Scrubs the breadcrumb and performance span payloads of a recording event in
 * place.
 *
 * Used as the `beforeAddRecordingEvent` hook on the replay integration, which
 * only receives replay's own custom events, never the recorded DOM events.
 *
 * @param {*} event A `ReplayFrameEvent` from `@sentry/replay`.
 * @return {*} The same event, scrubbed.
 */
export function scrubRecordingEvent(event) {
  if (event.data && event.data.tag === 'breadcrumb') {
    if (event.data.payload) {
      event.data.payload = scrubBreadcrumb(event.data.payload);
    }
  }

  if (event.data && event.data.tag === 'performanceSpan') {
    const payload = event.data.payload;

    if (payload) {
      if (payload.description) {
        payload.description = scrubUrl(payload.description);
      }

      if (payload.data && payload.data.previous) {
        payload.data.previous = scrubUrl(payload.data.previous);
      }
    }
  }

  return event;
}

/**
 * Scrubs the `urls` list of a replay event, and the request URL of any event,
 * in place.
 *
 * Replay events skip `beforeSend` and `beforeSendTransaction`, so this is
 * registered as a global event processor instead.
 *
 * @param {!Sentry.Event} event The Sentry event.
 * @return {!Sentry.Event} The same event, scrubbed.
 */
export function scrubReplayEvent(event) {
  if (event.type === 'replay_event' && event.urls) {
    event.urls = event.urls.map((url) => scrubUrl(url));
  }

  return scrubRequest(event);
}

/**
 * Scrubs the transaction name, span descriptions and URLs, and the request
 * URL and Referer header of a transaction event in place.
 *
 * Used as the `beforeSendTransaction` hook.
 *
 * @param {!Sentry.Event} event The Sentry transaction event.
 * @return {!Sentry.Event} The same event, scrubbed.
 */
export function scrubTransaction(event) {
  if (event.transaction) {
    event.transaction = scrubUrl(event.transaction);
  }

  (event.spans || []).forEach((span) => {
    if (span.description) {
      span.description = scrubUrl(span.description);
    }

    if (span.data && span.data.url) {
      span.data.url = scrubUrl(span.data.url);
    }
  });

  return scrubRequest(event);
}
